import { useState, useEffect } from "react";
import styles from "./TotemSegment.module.css";

// Special ID for user's own answer
export const SELF_ANSWER_ID = "self_answer";

interface TotemSegmentProps {
  answerId: string;
  content: string;
  isNew?: boolean;
  onLike?: (answerId: string) => Promise<void>;
  onReport?: (answerId: string) => void;
  canLike?: boolean;
  hasLiked?: boolean;
  hideActions?: boolean; // Hide all action buttons (for archive view)
  likeCount?: number; // Display like count (for archive)
}

export function TotemSegment({
  answerId,
  content,
  isNew = false,
  onLike,
  onReport,
  canLike = false,
  hasLiked = false,
  hideActions = false,
  likeCount,
}: TotemSegmentProps) {
  const [isLiking, setIsLiking] = useState(false);
  const [liked, setLiked] = useState(hasLiked);

  // Check if this is the user's own answer
  const isSelfAnswer = answerId === SELF_ANSWER_ID;

  // Sync liked state with prop
  useEffect(() => {
    setLiked(hasLiked);
  }, [hasLiked]);

  const handleLike = async () => {
    if (!onLike || !canLike || liked || isLiking || isSelfAnswer) return;

    setIsLiking(true);
    try {
      await onLike(answerId);
      setLiked(true);
    } catch (error) {
      console.error("Failed to like:", error);
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <div className={`${styles.segment} ${isNew ? styles.segmentNew : ""}`}>
      <div className={styles.content}>
        <p>{content}</p>
      </div>

      {isSelfAnswer ? (
        <div className={styles.selfAnswerBadge}>Your answer</div>
      ) : !hideActions ? (
        <div className={styles.actions}>
          {canLike && (
            <button
              className={`${styles.actionBtn} ${liked ? styles.liked : ""}`}
              onClick={handleLike}
              disabled={liked || isLiking}
              aria-label={liked ? "Liked" : "Like this answer"}
            >
              <HeartIcon filled={liked} />
            </button>
          )}

          <button
            className={styles.actionBtn}
            onClick={() => onReport?.(answerId)}
            aria-label="Report this answer"
          >
            <FlagIcon />
          </button>

          <button className={styles.actionBtn} aria-label="Share this answer">
            <ShareIcon />
          </button>
        </div>
      ) : likeCount !== undefined && likeCount > 0 ? (
        <div className={styles.likeCountDisplay}>
          <HeartIcon filled={true} />
          <span>{likeCount}</span>
        </div>
      ) : null}

      {/* Decorative totem patterns */}
      <div className={styles.pattern}>
        <div className={styles.patternLine}></div>
        <div className={styles.patternDot}></div>
        <div className={styles.patternLine}></div>
      </div>
    </div>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      </svg>
    );
  }
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function FlagIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}
